import React, { memo } from 'react';
import { useSelector } from 'react-redux';

import { Box } from '@mui/material';
import PageBlock from '@sharedCR/common/PageBlock';
import DataDisplay, {
  useResponsiveDataDisplayVariant,
} from '@sharedCR/coreDesignSystem/DataDisplay';
import TooltipWrapper from 'app/shared/components/common/TooltipWrapper';
import { WithLoadingPropTypes } from 'hocs/withLoading';

import useTruncatedGeneral from 'hooks/useTruncatedGeneral';

import { selectUnderwritingQualificationDetails } from 'store/loanApplications/selectors';

import { UnderwritingTestIds } from '../Underwriting.testIds';

import { getSummaryInfoConfig } from './UnderwritingSummary.config';
import {
  getSummaryWrapperStyles,
  pageBlockClasses,
  summaryCardStyles,
} from './UnderwritingSummary.styles';

const SUMMARY_SKELETONS_LENGTH = 2;

const UnderwritingSummary = ({ isLoading }) => {
  const qualificationDetailsData = useSelector(selectUnderwritingQualificationDetails) || {};

  const [onMouseOver, onResetTooltipState, TruncatedProvider] = useTruncatedGeneral();

  const summaryInfo = getSummaryInfoConfig(qualificationDetailsData);

  const dataDisplayVariant = useResponsiveDataDisplayVariant();

  return (
    <PageBlock
      classes={pageBlockClasses}
      data-testid={UnderwritingTestIds.SUMMARY_SECTION}
      title="Underwriting"
    >
      <TruncatedProvider />
      <Box
        sx={getSummaryWrapperStyles(isLoading ? SUMMARY_SKELETONS_LENGTH : summaryInfo.length)}
        onMouseLeave={onResetTooltipState}
        onMouseOver={onMouseOver}
      >
        {isLoading
          ? Array.from({ length: SUMMARY_SKELETONS_LENGTH }, (_, index) => (
              <Box key={index} sx={summaryCardStyles}>
                <DataDisplay isLoading title="" value="" variant={dataDisplayVariant} />
              </Box>
            ))
          : summaryInfo.map(
              ({
                name,
                value,
                icon,
                iconColor,
                isModified,
                withInfoIcon,
                disableInteractive,
                hoverContent,
                customTooltipStyles,
              }) => {
                const card = (
                  <Box sx={summaryCardStyles}>
                    <DataDisplay
                      icon={icon}
                      iconColor={iconColor}
                      isAdditionalIcon={withInfoIcon}
                      isModified={isModified}
                      title={name}
                      value={value}
                      variant={dataDisplayVariant}
                    />
                  </Box>
                );

                return hoverContent ? (
                  <TooltipWrapper
                    disableInteractive={disableInteractive}
                    key={name}
                    title={hoverContent}
                    tooltipSx={customTooltipStyles}
                  >
                    {card}
                  </TooltipWrapper>
                ) : (
                  <React.Fragment key={name}>{card}</React.Fragment>
                );
              },
            )}
      </Box>
    </PageBlock>
  );
};

UnderwritingSummary.propTypes = {
  ...WithLoadingPropTypes,
};

export default memo(UnderwritingSummary);
